import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiX, FiCopy, FiSmartphone, FiMail, FiCheck, FiSave, FiDownload, FiUsers } from 'react-icons/fi';
import QRCode from 'qrcode';
import { toast } from 'react-toastify';
import { formsAPI } from '../services/api';
import UserShareModal from './UserShareModal';

/** Use route param when form state omits `id` (e.g. FormBuilder). Strip SQL-style braces. */
const UUID_LIKE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function normalizeFormIdString(raw) {
  if (raw == null) return null;
  const s = String(raw)
    .replace(/[{}]/g, '')
    .replace(/^["']|["']$/g, '')
    .trim();
  if (!s || s === 'undefined' || s === 'null') return null;
  return UUID_LIKE.test(s) ? s : null;
}

/**
 * @param {object} form - may include id from API
 * @param {string|undefined} routeFormId - id from /builder/:id (preferred when form.id missing)
 */
export function resolveFormIdForApi(form, routeFormId) {
  return (
    normalizeFormIdString(routeFormId) ||
    normalizeFormIdString(form?.id) ||
    normalizeFormIdString(form?.form_id) ||
    null
  );
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const ShareSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 12px 0;
`;

const URLContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
`;

const URLInput = styled.input`
  flex: 1;
  border: none;
  background: none;
  font-size: 14px;
  color: #0ea5e9;
  outline: none;
  font-family: 'Courier New', monospace;
  cursor: pointer;
  text-decoration: underline;
  
  &:hover {
    color: #0284c7;
  }
`;

const CopyButton = styled.button`
  background: #0ea5e9;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: #0284c7;
  }

  &:active {
    transform: translateY(1px);
  }
`;

const QRCodeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const QRCodeImage = styled.img`
  width: 150px;
  height: 150px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  padding: 8px;
`;

const QRCodeLabel = styled.p`
  margin: 12px 0 0 0;
  font-size: 14px;
  color: #6b7280;
  text-align: center;
`;

const ShareButtons = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const ShareButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
  }

  &:active {
    transform: translateY(1px);
  }
`;

const CustomURLContainer = styled.div`
  margin-bottom: 20px;
`;

const CustomURLInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 8px;
  
  &:focus {
    outline: none;
    border-color: #0ea5e9;
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
  }
`;

const UpdateURLButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #059669;
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const URLHint = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 4px 0 0 0;
`;

const FormInfo = styled.div`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
`;

const FormTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #1e40af;
  margin: 0 0 4px 0;
`;

const FormDescription = styled.p`
  font-size: 14px;
  color: #1e40af;
  margin: 0;
  opacity: 0.8;
`;

const ShareModal = ({ isOpen, onClose, form, routeFormId }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [isGeneratingQR, setIsGeneratingQR] = useState(false);
  const [customUrl, setCustomUrl] = useState(form?.custom_url || '');
  const [isUpdatingUrl, setIsUpdatingUrl] = useState(false);
  const [showUserShareModal, setShowUserShareModal] = useState(false);

  const effectiveFormId = resolveFormIdForApi(form, routeFormId);

  // Generate the shareable URL - ensure it's a full URL with protocol
  // Always use the current protocol (http:// or https://) for proper link recognition
  const getShareUrl = () => {
    if (!effectiveFormId) return '';
    
    // Get the current origin (protocol + host)
    const origin = window.location.origin;
    
    // Build the URL
    let url;
    if (customUrl && customUrl.trim()) {
      // Use custom URL (encode it properly)
      const cleanCustomUrl = customUrl.trim().replace(/^\/+|\/+$/g, ''); // Remove leading/trailing slashes
      url = `${origin}/form/${encodeURIComponent(cleanCustomUrl)}`;
    } else {
      // Use form ID
      url = `${origin}/form/${effectiveFormId}`;
    }
    
    // Ensure URL is clean (no double slashes except after protocol)
    url = url.replace(/([^:]\/)\/+/g, '$1');
    
    return url;
  };
  
  const finalShareUrl = getShareUrl();

  // Generate QR code
  useEffect(() => {
    if (isOpen && effectiveFormId) {
      setIsGeneratingQR(true);
      QRCode.toDataURL(finalShareUrl, {
        width: 150,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      })
      .then(url => {
        setQrCodeUrl(url);
        setIsGeneratingQR(false);
      })
      .catch(err => {
        console.error('Error generating QR code:', err);
        setIsGeneratingQR(false);
      });
    }
  }, [isOpen, effectiveFormId, finalShareUrl]);

  // Copy URL to clipboard with proper formatting
  const copyToClipboard = (url, label) => {
    if (!url) {
      toast.error('No URL to copy');
      return;
    }
    
    // Ensure URL is properly formatted with protocol
    let formattedUrl = url.trim();
    
    // If URL doesn't start with http:// or https://, add protocol
    if (!formattedUrl.match(/^https?:\/\//i)) {
      const origin = window.location.origin;
      if (formattedUrl.startsWith('//')) {
        formattedUrl = `${window.location.protocol}${formattedUrl}`;
      } else if (formattedUrl.startsWith('/')) {
        formattedUrl = `${origin}${formattedUrl}`;
      } else {
        formattedUrl = `${origin}/${formattedUrl}`;
      }
    }
    
    // Clean up the URL - remove any double slashes except after protocol
    formattedUrl = formattedUrl.replace(/([^:]\/)\/+/g, '$1');
    
    // Remove any trailing whitespace
    formattedUrl = formattedUrl.trim();
    
    navigator.clipboard.writeText(formattedUrl).then(() => {
      toast.success(`${label} copied to clipboard!`);
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = formattedUrl;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          toast.success(`${label} copied to clipboard!`);
        } else {
          toast.error('Failed to copy to clipboard');
        }
      } catch (err) {
        console.error('Copy error:', err);
      toast.error('Failed to copy to clipboard');
      }
      document.body.removeChild(textArea);
    });
  };
  

  // Share functions
  const shareViaEmail = () => {
    const subject = `Form: ${form?.title || 'Untitled Form'}`;
    // Format body with clickable link on its own line for better email client recognition
    // Put link on separate line and ensure it has protocol
    const body = `Please fill out this form:\n\n${finalShareUrl}\n\nClick the link above to access the form.`;
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoUrl);
  };

  const shareViaWhatsApp = () => {
    const text = `Please fill out this form: ${finalShareUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, '_blank');
  };
  
  // Use Web Share API if available (for mobile devices)
  const shareViaNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: form?.title || 'Form',
          text: `Please fill out this form: ${form?.title || 'Untitled Form'}`,
          url: finalShareUrl
        });
        toast.success('Link shared successfully!');
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error sharing:', err);
          // Fallback to copy
          copyToClipboard(finalShareUrl, 'Form link');
        }
      }
    } else {
      // Fallback to copy if Web Share API not available
      copyToClipboard(finalShareUrl, 'Form link');
    }
  };

  const downloadQRCode = () => {
    if (!qrCodeUrl) {
      toast.error('QR code not generated yet');
      return;
    }

    // Create a link element to download the QR code
    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = `${form?.title || 'form'}-qr-code.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('QR code downloaded successfully');
  };

  const updateCustomUrl = async () => {
    if (!customUrl.trim()) {
      toast.error('Custom URL cannot be empty');
      return;
    }

    // Normalize: trim, lowercase, convert spaces to hyphens
    const normalized = customUrl.trim().toLowerCase().replace(/\s+/g, '-');
    // Replace problematic characters that break routing or server rules
    const safeSlug = normalized.replace(/&/g, 'and');

    // Validate custom URL using safe path-segment chars
    // Allowed: letters, numbers, '-', '.', '_', '~', '&'
    const urlPattern = /^[a-z0-9._~\-]+$/;
    if (!urlPattern.test(safeSlug)) {
      toast.error('Custom URL may include letters, numbers, hyphen (-), underscore (_), dot (.), and tilde (~).');
      return;
    }

    if (!effectiveFormId) {
      toast.error('Could not determine form id. Save the form and try again.');
      return;
    }

    try {
      setIsUpdatingUrl(true);
      await formsAPI.update(effectiveFormId, { custom_url: safeSlug });
      toast.success('Custom URL updated successfully');
      // Update the form object to reflect the change
      if (form) {
        form.custom_url = safeSlug;
      }
      setCustomUrl(safeSlug);
    } catch (error) {
      toast.error('Failed to update custom URL');
      console.error('Update URL error:', error);
    } finally {
      setIsUpdatingUrl(false);
    }
  };

  if (!isOpen || !form) return null;

  return (
    <ModalOverlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Share Form</ModalTitle>
          <CloseButton onClick={onClose}>
            <FiX size={20} />
          </CloseButton>
        </ModalHeader>

        <FormInfo>
          <FormTitle>{form.title || 'Untitled Form'}</FormTitle>
          <FormDescription>{form.description || 'No description provided'}</FormDescription>
        </FormInfo>

        <CustomURLContainer>
          <SectionTitle>Custom URL</SectionTitle>
          <CustomURLInput
            type="text"
            value={customUrl}
            onChange={(e) => setCustomUrl(e.target.value)}
            placeholder="my-custom-form-url"
          />
          <UpdateURLButton onClick={updateCustomUrl} disabled={isUpdatingUrl}>
            {isUpdatingUrl ? <FiSave className="spinner" /> : <FiCheck />}
            {isUpdatingUrl ? 'Updating...' : 'Update URL'}
          </UpdateURLButton>
          <URLHint>
            Leave empty to use the default URL. Allowed: letters, numbers, -, _, ., ~. “&” will be saved as "and". Spaces become hyphens.
          </URLHint>
        </CustomURLContainer>

        <ShareSection>
          <SectionTitle>Form Link</SectionTitle>
          <URLContainer>
            <URLInput
              value={finalShareUrl}
              readOnly
              onClick={(e) => {
                e.target.select();
                // Also copy to clipboard when clicked
                copyToClipboard(finalShareUrl, 'Form link');
              }}
              style={{ cursor: 'pointer' }}
              title="Click to select and copy the link"
            />
            <CopyButton onClick={() => copyToClipboard(finalShareUrl, 'Form link')}>
              <FiCopy size={16} />
              Copy
            </CopyButton>
          </URLContainer>
          <div style={{ 
            marginTop: '8px', 
            fontSize: '12px', 
            color: '#6b7280',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap'
          }}>
            <a 
              href={finalShareUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                color: '#0ea5e9',
                textDecoration: 'underline',
                fontWeight: '500',
                cursor: 'pointer'
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              Open Link ↗
            </a>
            <span style={{ color: '#d1d5db' }}>|</span>
            <span>Click the URL above or use "Open Link" to test</span>
          </div>
          <div style={{ 
            marginTop: '8px', 
            padding: '10px',
            background: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#0369a1'
          }}>
            <strong>Tip:</strong> When sharing via email or messaging apps, paste the link directly. Most apps will automatically make it clickable if it starts with http:// or https://
          </div>
        </ShareSection>

        <ShareSection>
          <SectionTitle>QR Code</SectionTitle>
          <QRCodeContainer>
            {isGeneratingQR ? (
              <div style={{ 
                width: '150px', 
                height: '150px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }}>
                <div style={{ color: '#6b7280' }}>Generating...</div>
              </div>
            ) : (
              <>
                <QRCodeImage src={qrCodeUrl} alt="QR Code" />
                <QRCodeLabel>Scan to open form on mobile</QRCodeLabel>
                <CopyButton onClick={downloadQRCode} style={{ marginTop: '12px' }}>
                  <FiDownload size={16} />
                  Download QR Code
                </CopyButton>
              </>
            )}
          </QRCodeContainer>
        </ShareSection>

        <ShareSection>
          <SectionTitle>Share with Portal Users</SectionTitle>
          <ShareButton 
            onClick={() => setShowUserShareModal(true)}
            style={{ 
              width: '100%', 
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              fontWeight: '600',
              padding: '14px'
            }}
          >
            <FiUsers size={18} />
            Share with Portal Users
          </ShareButton>
          <URLHint style={{ marginTop: '8px', textAlign: 'center' }}>
            Give specific users access to view, edit, or manage responses
          </URLHint>
        </ShareSection>

        <ShareSection>
          <SectionTitle>Share Via</SectionTitle>
          <ShareButtons>
            <ShareButton onClick={shareViaEmail}>
              <FiMail size={16} />
              Email
            </ShareButton>
            <ShareButton onClick={shareViaWhatsApp}>
              <FiSmartphone size={16} />
              WhatsApp
            </ShareButton>
          </ShareButtons>
        </ShareSection>
      </ModalContent>

      {showUserShareModal && (
        <UserShareModal
          isOpen={showUserShareModal}
          onClose={() => setShowUserShareModal(false)}
          item={effectiveFormId && form ? { ...form, id: effectiveFormId } : form}
          type="form"
        />
      )}
    </ModalOverlay>
  );
};

export default ShareModal;
